import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Text,
    TextInput,
} from 'grommet';
import {
    API,
    resolveSortField,
    RowsPerPageSelect,
    usePaginatedSort,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { useDebouncedCallback } from 'use-debounce';

const SEARCH_DEBOUNCE_MS = 400;

export const SchoolsDashboard = () => {
    const [schools, setSchools] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort();

    // The input stays instant; only the debounced value drives a refetch.
    // A new term also drops back to page 1, since the current offset may not
    // exist in the narrowed set.
    const applySearchTerm = useDebouncedCallback((value) => {
        setAppliedSearchTerm(value);
        setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    useEffect(() => {
        if (!page) return;
        let isActive = true;

        (async () => {
            const response = await loadSchools({
                page,
                pageSize,
                sort,
                search: appliedSearchTerm,
            });
            if (isActive && response?.meta?.pagination) {
                setSchools(response.data);
                applyPagination(response.meta);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [page, pageSize, sort, appliedSearchTerm, applyPagination]);

    const dataColumns = [
        {
            header: 'School',
            property: 'name',
            render: (school) => (
                <Anchor
                    key={school.id}
                    href={`/schools/${school.id}`}
                    label={school.name}
                />
            ),
            sortable: true,
        },
        {
            header: 'Students',
            property: 'studentCount',
            render: (school) => <Text>{school.studentCount ?? 0}</Text>,
            sortable: true,
        },
        {
            header: 'Location',
            property: 'locationName',
            render: (school) => (
                <Text>
                    {school.locationName
                        ? `${school.locationName}, ${school.locationState ?? '?'}`
                        : '—'}
                </Text>
            ),
            sortable: true,
        },
        {
            header: 'Last Login',
            property: 'lastLogin',
            render: (school) => (
                <Text>
                    {school.lastLogin
                        ? new Date(school.lastLogin).toLocaleDateString()
                        : 'Never'}
                </Text>
            ),
            sortable: true,
        },
        {
            header: 'Reports Active',
            property: 'isReportClient',
            render: (school) => (
                <Text>{school.isReportClient ? 'Yes' : 'No'}</Text>
            ),
            sortable: true,
        },
        {
            header: 'Most Recent Sign Up',
            property: 'mostRecentSignUp',
            render: (school) => (
                <Text>
                    {school.mostRecentSignUp
                        ? new Date(
                              school.mostRecentSignUp,
                          ).toLocaleDateString()
                        : 'N/A'}
                </Text>
            ),
            sortable: true,
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box gap="small">
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="2">Schools</Heading>
                    </Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        gap="medium"
                        pad={{ right: 'small', bottom: 'small' }}>
                        <Box width="medium">
                            <TextInput
                                a11yTitle="Search schools"
                                placeholder="Search by school name"
                                value={searchTerm}
                                onChange={(event) => {
                                    setSearchTerm(event.target.value);
                                    applySearchTerm(event.target.value);
                                }}
                            />
                        </Box>
                    </Box>
                    <Box>
                        {schools.length === 0 ? (
                            <Box pad="medium" align="center">
                                <Text>No schools found</Text>
                            </Box>
                        ) : (
                            <DataTable
                                primaryKey="id"
                                sort={{ ...sort, external: true }}
                                onSort={handleSort}
                                data={schools}
                                columns={dataColumns}
                                border={{
                                    body: { side: 'bottom', color: 'border' },
                                }}
                            />
                        )}
                    </Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        justify="between"
                        gap="small"
                        pad="small">
                        <RowsPerPageSelect
                            options={stepOptions}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                        <Pagination
                            numberItems={totalItems}
                            page={page}
                            onChange={handlePageChange}
                            step={pageSize}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const SORT_FIELD_MAP = {
    name: 'name',
    studentCount: 'student_count',
    isReportClient: 'is_report_client',
    lastLogin: 'last_login',
    mostRecentSignUp: 'most_recent_sign_up',
    locationName: 'location_name',
};

const loadSchools = async ({ page, pageSize, sort, search } = {}) => {
    const sortBy = resolveSortField(sort, SORT_FIELD_MAP, 'name');
    const sortDir = sort?.direction ?? 'asc';
    const term = search?.trim();
    const query = QueryString.stringify(
        {
            page,
            pageSize,
            sortBy,
            sortDir,
            ...(term ? { search: term } : {}),
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/schools/list?${query}`);
        return { data: response.data.data, meta: response.data.meta };
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0, page } } };
};
